import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Legacy: chiave localStorage dove veniva salvato il JWT come fallback Bearer
// (storicamente per Safari iOS ITP cross-site, quando front e back stavano su
// host diversi). Audit ondata 5, E4-F1: ora che frontend e backend sono
// same-origin il solo cookie httpOnly `access_token` basta, e il JWT in
// localStorage era leggibile da un XSS → vanificava l'httpOnly. Non lo salviamo
// più; qui puliamo UNA volta l'eventuale token legacy rimasto nei browser dai
// vecchi bundle.
const LEGACY_TOKEN_KEY = 'clab_access_token';
const clearLegacyToken = () => { try { localStorage.removeItem(LEGACY_TOKEN_KEY); } catch {} };
clearLegacyToken();

// Interceptor globale axios: invia SEMPRE i cookie (same-origin) su ogni
// richiesta. Nessuna iniezione di Authorization Bearer: l'auth viaggia solo nel
// cookie httpOnly. Installato a livello modulo (una sola volta, non nel render).
axios.interceptors.request.use((config) => {
  if (config.withCredentials === undefined) config.withCredentials = true;
  return config;
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const attempt = () => axios.get(`${BACKEND_URL}/api/auth/me`, {
      withCredentials: true,
      timeout: 10000,            // niente attesa infinita se il backend non risponde
    });
    try {
      let data;
      try {
        ({ data } = await attempt());
      } catch (err) {
        // Backend momentaneamente irraggiungibile (es. restart durante il deploy):
        // un retry dopo breve attesa evita di restare bloccati sul loader d'avvio.
        const status = err?.response?.status;
        if (status === undefined || status >= 500) {
          await new Promise((r) => setTimeout(r, 2500));
          ({ data } = await attempt());
        } else {
          throw err;            // 401/403 ecc.: utente non autenticato, nessun retry
        }
      }
      setUser(data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { email, password, remember_me: rememberMe },
        { withCredentials: true }
      );
      // E4-F1: nessun salvataggio del token in localStorage — l'auth sta nel
      // cookie httpOnly settato dal backend (che non lo ritorna più nel body).
      setUser(data);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message,
      };
    }
  };

  const register = async (email, password, name) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/register`,
        { email, password, name },
        { withCredentials: true }
      );
      // NON fare setUser: l'account è disabilitato finché un admin non lo approva.
      // Il backend non setta cookie su register, quindi non c'è sessione da attivare.
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message,
      };
    }
  };

  // Cambio password dell'utente autenticato. Al successo il backend azzera
  // must_change_password: lo riflettiamo in-place così il gate si sblocca.
  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { withCredentials: true }
      );
      setUser(prev => (prev ? { ...prev, must_change_password: false } : prev));
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message,
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearLegacyToken();
      setUser(false);
    }
  };

  // Persistenza preferenze utente server-side. Aggiorna il doc utente in DB
  // e in-place lo stato locale, cosi' anche fra una sessione e l'altra
  // (incognito incluso) la scelta sopravvive.
  const setUserPref = async (key, value) => {
    try {
      await axios.put(`${BACKEND_URL}/api/auth/me/prefs`, { key, value }, { withCredentials: true });
      setUser(prev => prev ? { ...prev, prefs: { ...(prev.prefs || {}), [key]: value } } : prev);
    } catch (error) {
      console.warn('setUserPref fallita', key, error?.message);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, setUserPref, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
};
